import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToaster from 'actions/useToaster';
import CustomDate from 'CustomDate';
import { formatDicomName } from 'dicom';
import { Badge, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { BurnerJob } from 'reducers/printersReducer';
import getRimagePrioritiesOptions from '../RimageUtils';
import React, { useEffect, useState } from 'react';
import { faChevronRight, faChevronDown, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import JobActions from './JobsActions';
import {
  ellipseTooltipped,
  handleUpdatePriority,
  isPriorityChangeable,
  setupOriginalJob,
  tooltipped,
} from './JobsTableUtils';

type LinkedJobRowProps = {
  job: BurnerJob;
  index: number;
  support: BurnerJob[];
  cancelJob: (jobId: string, burner: string) => void;
};

function LinkedJobRow({ job, index, support, cancelJob }: LinkedJobRowProps) {
  const toaster = useToaster();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [hovered, setHovered] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<string>('');
  const [detailedErrorState, setDetailedErrorState] = useState<string>('');
  const [originalJob, setOriginalJob] = useState<BurnerJob>();

  function updateErrorState() {
    const model = job.burnerModel;
    const errorList: Array<string> = [];
    const errorDescriptionList: Array<string> = [];
    const error = support.forEach(element => {
      if (model === 'EPSON_P100') {
        if (parseInt(element.statusCode) >= 5) {
          errorList.push(element.errorCode);
          errorDescriptionList.push(element.errorDescription);
        }
      } else if (model === 'RIMAGE_2000I') {
        if (element.statusCode === 'ERR') {
          errorList.push(element.errorCode);
          errorDescriptionList.push(element.errorDescription);
        }
      }
    });
    if (errorList.length > 0) {
      let firstError: string = '';
      let firstErrorDescription: string = '';
      let differentErrors: boolean = false;
      for (let i = 0; i < errorList.length; i++) {
        if (i > 0) {
          if (!(firstError === errorList[i] && firstErrorDescription === errorDescriptionList[i])) {
            differentErrors = true;
            break;
          }
        } else {
          firstError = errorList[i];
          firstErrorDescription = errorDescriptionList[i];
        }
      }
      if (differentErrors) {
        setErrorState('ERR');
        setDetailedErrorState('Multiple errors');
      } else {
        setErrorState(firstError);
        setDetailedErrorState(firstErrorDescription);
      }
    } else {
      setErrorState('');
      setDetailedErrorState('');
    }
  }

  function updateoriginalJob() {
    const agglomerate: BurnerJob = setupOriginalJob(originalJob, job, support);
    setOriginalJob(agglomerate);
  }

  useEffect(() => {
    updateErrorState();
    updateoriginalJob();
  }, [job, support]);

  const filteredInstancesTooltip = (jobData: BurnerJob) => {
    return (
      <Tooltip id="tooltip">
        <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
          {jobData.filteredInstances + ' instances have been filtered'}
        </p>
      </Tooltip>
    );
  };

  return (
    <>
      <tr
        key={job.id + '_' + index}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        style={{ backgroundColor: hovered ? '#4d535a' : 'var(--gray-700)' }}
      >
        <td>
          <FontAwesomeIcon
            key={job.id + 'Expand'}
            onClick={() => {
              setExpanded(!expanded);
            }}
            icon={expanded ? faChevronDown : faChevronRight}
            style={{
              color: '#ebedef',
              width: '12px',
              height: '12px',
              backgroundColor: 'transparent',
            }}
          />
        </td>
        <td>{ellipseTooltipped(job.id)}</td>
        <td>{tooltipped(formatDicomName(job.patientName))}</td>
        <td>
          {job.studies.map(study => {
            if (study.description?.trim() === '') {
              return (
                <div key={study.studyUid + '_container'}>
                  <i key={study.studyUid}>NO_DESCRIPTION</i>
                </div>
              );
            }
            return <div key={study.studyUid}>{tooltipped(study.description)}</div>;
          })}
        </td>
        <td>
          {job.studies.map(study => {
            if (!study.series || study.series.length === 0) return <div key={study.studyUid}>ALL</div>;
            return <div key={study.studyUid}>{'[' + study.series.length + '/' + study.totalSeries + ']'}</div>;
          })}
        </td>
        <td>{CustomDate.fromString(job.date).toDateTimeString()}</td>
        <td>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {tooltipped(job.statusDescription, job.detailedStatusDescription)}
            {originalJob && originalJob.filteredInstances > 0 && (
              <div style={{ marginLeft: '0.3em' }}>
                <OverlayTrigger placement="bottom" overlay={filteredInstancesTooltip(originalJob!)}>
                  <FontAwesomeIcon icon={faTriangleExclamation} className="warn-info-icon" />
                </OverlayTrigger>
              </div>
            )}
          </div>
        </td>
        <td>
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={props => (
              <Tooltip id="job-error-tooltip" {...props}>
                <div style={{ fontSize: '16px' }} key={job.id + '_error'}>
                  {job.errorDescription}
                </div>
              </Tooltip>
            )}
          >
            <Badge pill variant={job.statusDescription === 'Being processed' ? 'warning' : 'danger'}>
              {errorState === '' ? job.errorCode : tooltipped(errorState, detailedErrorState)}
            </Badge>
          </OverlayTrigger>
        </td>
        <td>
          <div>
            <Form.Control
              as="select"
              style={{
                maxWidth: '5em',
                minWidth: '5em',
                paddingLeft: '2px',
                paddingRight: '3px',
                paddingTop: '0',
                paddingBottom: '0',
                marginLeft: '0',
              }}
              className="selectBoxNum selectFilter"
              defaultValue={
                job.burnerModel === 'EPSON_P100'
                  ? job.priority !== undefined
                    ? 'High'
                    : '-'
                  : job.burnerModel === 'RIMAGE_2000I'
                  ? job.priority + ''
                  : undefined
              }
              onChange={e => {
                if (e.target.value !== job.priority) {
                  handleUpdatePriority(job, e.target.value, toaster);
                }
              }}
              disabled={!isPriorityChangeable(job)}
            >
              {job.burnerModel === 'EPSON_P100' ? (
                <React.Fragment>
                  <option value={'HIGH'}>High</option>
                  <option value={'-'}>-</option>
                </React.Fragment>
              ) : job.burnerModel === 'RIMAGE_2000I' ? (
                getRimagePrioritiesOptions()
              ) : (
                <React.Fragment></React.Fragment>
              )}
            </Form.Control>
          </div>
        </td>
        <td>
          <Row style={{ justifyContent: 'center' }}>
            <JobActions job={originalJob!} cancelJob={cancelJob} />
          </Row>
        </td>
      </tr>
      {support.map(subJob => (
        <tr
          key={subJob.id + '_' + index}
          style={{ display: expanded ? 'table-row' : 'None', backgroundColor: hovered ? '#4d535a' : 'var(--gray-700)' }}
          onMouseEnter={() => {
            setHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
          }}
        >
          <td />
          <td>{ellipseTooltipped(subJob.id)}</td>
          <td>{tooltipped(formatDicomName(subJob.patientName))}</td>
          <td>
            {subJob.studies.map(study => {
              if (study.description?.trim() === '') {
                return (
                  <div key={study.studyUid + '_container'}>
                    <i key={study.studyUid}>NO_DESCRIPTION</i>
                  </div>
                );
              }
              return <div key={study.studyUid}>{tooltipped(study.description)}</div>;
            })}
          </td>
          <td>
            {subJob.studies.map(study => {
              if (!study.series || study.series.length === 0) return <div key={study.studyUid}>ALL</div>;
              return <div key={study.studyUid}>{'[' + study.series.length + '/' + study.totalSeries + ']'}</div>;
            })}
          </td>
          <td>{CustomDate.fromString(subJob.date).toDateTimeString()}</td>
          <td>{tooltipped(subJob.statusDescription, subJob.detailedStatusDescription)}</td>
          <td>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={props => (
                <Tooltip id="job-error-tooltip" {...props}>
                  <div style={{ fontSize: '16px' }} key={subJob.id + '_error'}>
                    {subJob.errorDescription}
                  </div>
                </Tooltip>
              )}
            >
              <Badge pill variant={subJob.statusDescription === 'Being processed' ? 'warning' : 'danger'}>
                {subJob.errorCode}
              </Badge>
            </OverlayTrigger>
          </td>
          <td />
          <td />
        </tr>
      ))}
    </>
  );
}
export default LinkedJobRow;
